import React, { useCallback, useEffect, useState } from "react";
import DropdownResults from "./dropdownResults/DropdownResults";
import styles from "./CompanySearchInput.module.scss";
import {
  CompanyProposition,
  useFetchAutocompleteCompanies,
} from "./hooks/useFetchAutocompleteCompanies";

interface CompanySearchInputProps {
  placeholder: string;
  onSelectCompany: (company: CompanyProposition) => void;
  selectedCompany?: CompanyProposition | null;
  displaySiret?: boolean;
}

const CompanySearchInput: React.FC<CompanySearchInputProps> = ({
  placeholder,
  onSelectCompany,
  selectedCompany,
  displaySiret = false,
}) => {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState<string>("");
  const [dropdownVisible, setDropdownVisible] = useState<boolean>(false);

  useEffect(() => {
    if (displaySiret && selectedCompany?.siret !== undefined) {
      setSearchQuery(selectedCompany.siret);

      return;
    }
    if (selectedCompany?.businessName !== undefined) {
      setSearchQuery(selectedCompany.businessName);
    }
  }, [displaySiret, selectedCompany]);

  const { companies, isLoading, isError } =
    useFetchAutocompleteCompanies(debouncedSearchQuery);

  const debounce = useCallback((input: string, delay: number) => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(input);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, []);

  useEffect(() => {
    if (displaySiret && searchQuery !== selectedCompany?.siret) {
      return debounce(searchQuery, 500);
    }
    if (!displaySiret && searchQuery !== selectedCompany?.businessName) {
      return debounce(searchQuery, 500);
    }
  }, [
    searchQuery,
    debounce,
    debouncedSearchQuery,
    selectedCompany?.businessName,
    selectedCompany?.siret,
    displaySiret,
  ]);

  useEffect(() => {
    setDropdownVisible(debouncedSearchQuery.length > 0 && companies.length > 0);
  }, [debouncedSearchQuery, companies]);

  const changeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleCompanySelect = (company: CompanyProposition) => {
    onSelectCompany(company);
    setSearchQuery(company.businessName);
    setDropdownVisible(false);
  };

  return (
    <div className={styles.inputWrapper}>
      <input
        type="text"
        value={searchQuery}
        onChange={changeHandler}
        placeholder={placeholder}
        autoComplete="on"
        className={styles.input}
        onFocus={() =>
          setDropdownVisible(
            debouncedSearchQuery.length > 3 && companies.length > 0
          )
        }
        onBlur={() => setTimeout(() => setDropdownVisible(false), 200)}
      />
      {dropdownVisible && (
        <DropdownResults
          companies={companies}
          isLoading={isLoading}
          isError={isError}
          onSelectCompany={handleCompanySelect}
        />
      )}
    </div>
  );
};

export default CompanySearchInput;
